import { Link, useNavigate } from 'react-router-dom';
import { useEngineerList } from '../../api/engineers';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import { Users } from 'lucide-react';
import { TablePageSkeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import ErrorState from '../../components/ui/ErrorState';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export default function EngineersPage() {
  const navigate = useNavigate();
  const { data: engineers, isLoading, error, refetch } = useEngineerList();

  if (isLoading) return <TablePageSkeleton cols={6} hasAction={false} />;
  if (error)     return <ErrorState onRetry={refetch} />;

  const active   = engineers?.filter((e) => e.isActive)  ?? [];
  const inactive = engineers?.filter((e) => !e.isActive) ?? [];

  return (
    <div>
      <PageHeader title="Engineers" description="Workload at a glance" />

      <Card>
        {active.length === 0 && inactive.length === 0 ? (
          <EmptyState icon={Users} title="No engineers yet" description="Engineers appear here once they activate their account." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Team</TableHead>
                <TableHead className="text-right">Baseline pts</TableHead>
                <TableHead className="text-right">Cycle days</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...active, ...inactive].map((eng) => (
                <TableRow key={eng.id} className="cursor-pointer" onClick={() => navigate(`/engineers/${eng.id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                        {eng.name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()}
                      </div>
                      <div>
                        <Link to={`/engineers/${eng.id}`} className="font-medium text-foreground hover:text-primary" onClick={(e) => e.stopPropagation()}>
                          {eng.name}
                        </Link>
                        <p className="mt-0.5 text-xs text-muted-foreground">{eng.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize text-muted-foreground">
                    {eng.role.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{eng.team ?? '—'}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{eng.baselinePoints}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{eng.baselineCycleDays}</TableCell>
                  <TableCell>
                    <Badge label={eng.isActive ? 'active' : 'inactive'} variant={eng.isActive ? 'green' : 'gray'} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
